import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { Grid, Snackbar, CircularProgress } from '@material-ui/core';
import { oneDayMs, snackbarDuration } from '../constants/general';
import {
  formatDateOnlyStringFromLocalTime,
  nameToValueMap
} from '../helpers/helpers';

import i18n from '../i18n';
import EcommDataStudioTable from '../components/EcommDataAssignment/EcommDataStudioTable';
import EcommDataAccTable from '../components/EcommDataAssignment/EcommDataAccTable';
import EcommDataAccPrepTable from '../components/EcommDataAssignment/EcommDataAccPrepTable';
import EcommDataPrepTable from '../components/EcommDataAssignment/EcommDataPrepTable';
import EcommDataCopyTable from '../components/EcommDataAssignment/EcommDataCopyTable';
import EcommDataCopyPrepTable from '../components/EcommDataAssignment/EcommDataCopyPrepTable';
import EcommDataTranslationTable from '../components/EcommDataAssignment/EcommDataTranslationTable';
import EcommDataRetouchTable from '../components/EcommDataAssignment/EcommDataRetouchTable';
import {
  fetchEcommDailyAssignmentData,
  fetchSpecialProjects,
  saveOneEcommDailyAssignment,
  fetchLocationTypes
} from '../helpers/externalRequests';
import {
  LOCATION_TYPES
} from '../constants/booking';
import '../assets/stylesheets/Ecomm.css';
import { UserType } from '../validation';

class EcommView extends Component {
  constructor(props) {
    super(props);
    const { date } = this.props;
    this.state = {
      isFetchingData: true,
      date: date || new Date(),
      ecommData: {},
      locationTypes: [],
      error: false,
      success: false,
      specialProjectOptions: []
    };
  }

  componentDidMount = () => {
    fetchSpecialProjects()
      .then(allSpecialProjects => {
        this.setState({
          specialProjectOptions: this.formatSpecialProjectDropdown(
            allSpecialProjects.data
          )
        });
        this.fetchAny(new Date());
      })
      .catch(() => {
        this.setState({
          isFetchingData: false,
          error: true
        });
      });
  };

  formatSpecialProjectDropdown = allSpecialProjects => {
    const options = [{ displayValue: i18n.t('none'), value: -1 }];
    allSpecialProjects.forEach(specialProject => {
      options.push({
        displayValue: specialProject.specialProjectName,
        value: specialProject.id
      });
    });
    return options;
  };

  mapNameToValue = responseData => {
    return responseData.map(nameToValueMap);
  };

  fetchAny = async (date) => {
    const formattedDate = formatDateOnlyStringFromLocalTime(date);
    const locationTypesPromise = fetchLocationTypes()
    const ecommDataPromise = fetchEcommDailyAssignmentData(formattedDate);
    this.setState({
      ecommData: (await ecommDataPromise).data,
      locationTypes: (await locationTypesPromise).data,
      isFetchingData: false
    });
  };

  fetchPrevious = () => {
    this.setState(
      prevState => ({
        isFetchingData: true,
        date: new Date(prevState.date.getTime() - oneDayMs)
      }),
      () => {
        const { date } = this.state;
        this.fetchAny(date);
      }
    );
  };

  fetchNext = () => {
    this.setState(
      prevState => ({
        isFetchingData: true,
        date: new Date(prevState.date.getTime() + oneDayMs)
      }),
      () => {
        const { date } = this.state;
        this.fetchAny(date);
      }
    );
  };

  getNow = () =>
    new Date();
  
  handleDatePickerChange = datePick => {
    this.setState(
      {
        isFetchingData: true,
        date: new Date(datePick)
      },
      () => {
        const { date } = this.state;
        this.fetchAny(date);
      }
    );
  };

  saveOne = data => {
    const { locationType } = data;
    saveOneEcommDailyAssignment(locationType, data)
    .catch(() => {
      this.setState({
        error: true
      });
    });
  }

  closeSnackBar = () => {
    this.setState({ success: false });
  };

  openSnackBar = () => {
    this.setState({ error: false });
  };

  getLocationType = (locationType) => {
    const { locationTypes } = this.state;
    return locationTypes.find(locType => locType.locationTypeName === locationType.name);
  };

  render() {
    const {
      date,
      isFetchingData,
      ecommData,
      error,
      success,
      specialProjectOptions,
    } = this.state;

    const { 
      user,
    } = this.props;

    const canEdit = user.scopes && user.scopes.includes('activities:write')

    return (
      <Grid container direction="column" className="ecommView">
        <Grid item xs={12} className='ecommDataGrid'>
          {isFetchingData ? (
            <div>
              <CircularProgress color="secondary" />
              <span>{i18n.t('loading')}</span>
            </div>
          ) : (
            <EcommDataPrepTable
              prepData={ecommData.prepData || []}
              locationType={this.getLocationType(LOCATION_TYPES.PREP)}
              saveOne={this.saveOne}
              handleDatePickerChange={this.handleDatePickerChange}
              date={date}
              fetchPrevious={this.fetchPrevious}
              fetchNext={this.fetchNext}
              specialProjectOptions={specialProjectOptions}
              getNow={this.getNow}
              canEdit={canEdit}
            />
          )}
        </Grid>
        <br />
        <br />
        <Grid item xs={12} className='ecommDataGrid'>
          {isFetchingData ? (
            <div>
              <CircularProgress color="secondary" />
              <span>{i18n.t('loading')}</span>
            </div>
          ) : (
            <EcommDataStudioTable
              studioData={ecommData.studioData || []}
              locationType={this.getLocationType(LOCATION_TYPES.STUDIO)}
              saveOne={this.saveOne}
              handleDatePickerChange={this.handleDatePickerChange}
              date={date}
              fetchPrevious={this.fetchPrevious}
              fetchNext={this.fetchNext}
              specialProjectOptions={specialProjectOptions}
              getNow={this.getNow}
              canEdit={canEdit}
            />
          )}
        </Grid>
        <br />
        <br />
        <Grid item xs={12} className='ecommDataGrid'>
          {isFetchingData ? (
            <div>
              <CircularProgress color="secondary" />
              <span>{i18n.t('loading')}</span>
            </div>
          ) : (
            <EcommDataAccPrepTable
              accPrepData={ecommData.accPrepData || []}
              relatedLocationType={this.getLocationType(LOCATION_TYPES.SET)}
              saveOne={this.saveOne}
              handleDatePickerChange={this.handleDatePickerChange}
              date={date}
              fetchPrevious={this.fetchPrevious}
              fetchNext={this.fetchNext}
              specialProjectOptions={specialProjectOptions}
              getNow={this.getNow}
              canEdit={canEdit}
            />
          )}
        </Grid>
        <br />
        <br />
        <Grid item xs={12} className='ecommDataGrid'>
          {isFetchingData ? (
            <div>
              <CircularProgress color="secondary" />
              <span>{i18n.t('loading')}</span>
            </div>
          ) : (
            <EcommDataAccTable
              accData={ecommData.accData || []}
              locationType={this.getLocationType(LOCATION_TYPES.SET)}
              saveOne={this.saveOne}
              handleDatePickerChange={this.handleDatePickerChange}
              date={date}
              fetchPrevious={this.fetchPrevious}
              fetchNext={this.fetchNext}
              specialProjectOptions={specialProjectOptions}
              getNow={this.getNow}
              canEdit={canEdit}
            />
          )}
        </Grid>
        <br />
        <br />
        <Grid container className="ecommTableContainer">
          <Grid item className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataCopyPrepTable
                copyPrepData={ecommData.copyPrepData || []}
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
          <Grid item className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataCopyTable
                prepItemCount={ecommData.prepItemCount || 0}
                copyData={ecommData.copyData || []}
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4} className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataRetouchTable
                prepItemCount={ecommData.studioRetouchItemCount || 0}
                retouchData={ecommData.retouchData || []}
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
        </Grid>
        <br />
        <br />
        <Grid container className="ecommTableContainer">
          <Grid item className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataTranslationTable
                studioItemCount={ecommData.studioItemCount || 0}
                translationData={ecommData.frenchTranslationData || []}
                translationLanguage="French"
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                startTabIndex={700}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
          <Grid item className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataTranslationTable
                studioItemCount={ecommData.studioItemCount || 0}
                translationData={ecommData.japaneseTranslationData || []}
                translationLanguage="Japanese"
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                startTabIndex={800}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
          <Grid item className='ecommDataGrid'>
            {isFetchingData ? (
              <div>
                <CircularProgress color="secondary" />
                <span>{i18n.t('loading')}</span>
              </div>
            ) : (
              <EcommDataTranslationTable
                studioItemCount={ecommData.studioItemCount || 0}
                translationData={ecommData.chineseTranslationData || []}
                translationLanguage="Chinese"
                saveOne={this.saveOne}
                handleDatePickerChange={this.handleDatePickerChange}
                date={date}
                fetchPrevious={this.fetchPrevious}
                fetchNext={this.fetchNext}
                specialProjectOptions={specialProjectOptions}
                startTabIndex={900}
                getNow={this.getNow}
                canEdit={canEdit}
              />
            )}
          </Grid>
        </Grid>
        <Snackbar
          message={i18n.t('location:errorMsg')}
          open={error}
          onClose={this.openSnackBar}
          autoHideDuration={snackbarDuration}
        />
        <Snackbar
          message={i18n.t('location:successMsg')}
          open={success}
          onClose={this.closeSnackBar}
          autoHideDuration={snackbarDuration}
        />
      </Grid>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
});

EcommView.defaultProps = {
  date: new Date(),
}

EcommView.propTypes = {
  user: UserType.isRequired,
  date: PropTypes.instanceOf(Date),
};

// allows us to test methods of the component
export { EcommView as EcommViewNoConnect };

export default withLDConsumer()(connect(mapStateToProps)(EcommView));
